import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { counselingAdminApi } from '../../services/api'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'
import { formatDate, formatDateTime } from '../../utils/date'
import { useMarkNotificationsRead } from '../../hooks/useMarkNotificationsRead'
import { useAuth } from '../../context/AuthContext'

const STATUSES = ['new', 'reviewed', 'scheduled', 'completed', 'declined']

function formatTopic(value, t) {
  if (!value) return '—'
  const key = `counseling.topics.${value}`
  const translated = t(key)
  return translated === key ? value : translated
}

function formatTime(value, t) {
  if (!value) return '—'
  const key = `counseling.times.${value}`
  const translated = t(key)
  return translated === key ? value : translated
}

function formatProgram(value, t) {
  if (!value) return '—'
  if (value === 'undecided') return t('counseling.programs.undecided')
  return value
}

function formatCampus(value, t) {
  if (!value) return '—'
  if (value === 'no_preference') return t('counseling.campuses.noPreference')
  return value
}

export default function AdminCounselingDetail() {
  const { id } = useParams()
  const { t } = useTranslation()
  const { hasModuleAccess } = useAuth()
  const canEdit = hasModuleAccess('forms_counseling', 'can_edit')
  useMarkNotificationsRead('forms_counseling', id ? Number(id) : null)

  const [appointment, setAppointment] = useState(null)
  const [status, setStatus] = useState('new')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    counselingAdminApi
      .get(id)
      .then(({ data }) => {
        const item = data.data || data
        setAppointment(item)
        setStatus(item.status || 'new')
      })
      .catch(() => setAppointment(null))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [id])

  const handleSave = async () => {
    if (!canEdit) return

    setSaving(true)
    try {
      await counselingAdminApi.updateStatus(id, { status })
      load()
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingSpinner className="py-20" />
  if (!appointment) return null

  const detailFields = [
    ['parentName', appointment.parent_name],
    ['email', appointment.email],
    ['phone', appointment.phone],
    ['studentName', appointment.student_name],
    ['currentGrade', appointment.current_grade],
    ['program', formatProgram(appointment.program, t)],
    ['campus', formatCampus(appointment.campus, t)],
    ['topic', formatTopic(appointment.topic, t)],
    ['preferredDate', appointment.preferred_date ? formatDate(appointment.preferred_date) : '—'],
    ['preferredTime', formatTime(appointment.preferred_time, t)],
    ['message', appointment.message],
    ['submitted', appointment.created_at ? formatDateTime(appointment.created_at) : '—'],
  ]

  return (
    <div>
      <Link
        to="/cp-login/forms/counseling"
        className="mb-6 inline-flex items-center gap-2 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('admin.counseling.backToList')}
      </Link>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-primary">{appointment.student_name}</h2>
        <p className="text-text/60">{appointment.parent_name}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <h3 className="mb-4 font-semibold text-primary">{t('admin.counseling.details')}</h3>
          <dl className="grid gap-4 sm:grid-cols-2">
            {detailFields.map(([key, value]) => (
              <div key={key} className={key === 'message' ? 'sm:col-span-2' : ''}>
                <dt className="text-xs font-medium uppercase tracking-wide text-text/50">
                  {t(`admin.counseling.fields.${key}`)}
                </dt>
                <dd className="mt-1 whitespace-pre-wrap text-sm text-text">{value || '—'}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card>
          <h3 className="mb-4 font-semibold text-primary">{t('admin.forms.status')}</h3>
          {canEdit ? (
            <div className="space-y-4">
              <AdminFormField label={t('admin.forms.status')} htmlFor="counseling-status">
                <select
                  id="counseling-status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  className={adminInputClass}
                >
                  {STATUSES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </AdminFormField>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? t('common.loading') : t('admin.counseling.saveStatus')}
              </Button>
            </div>
          ) : (
            <p className="text-sm font-medium capitalize text-text">{appointment.status}</p>
          )}
        </Card>
      </div>
    </div>
  )
}
